import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, Sparkles, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { PublicSiteHeader } from '../../components/PublicSiteHeader';
import { PublicSiteFooter } from '../../components/PublicSiteFooter';
import { askApi, type AskClaimKind, type AskResponse } from '../../api/dataApi';
import { useFeatureStrict } from '../../lib/features';
import { track } from '../../lib/analytics';

type AskState = 'idle' | 'working' | 'done' | 'failed' | 'canceled';

const CLAIM_LABEL: Record<AskClaimKind, string> = {
  observation: 'Observation',
  correlation: 'Correlation',
  hypothesis: 'Hypothesis',
  recommendation: 'Recommendation',
};

const WINDOWS = [7, 30, 90];

// Ask Zmist: read-only, evidence-linked answers about a channel. Every claim
// carries its kind so interpretation is never shown as measured fact.
export function AskScreen() {
  const enabled = useFeatureStrict('ask.v1');
  const [params] = useSearchParams();
  const channel = params.get('channel') ?? '';
  const [question, setQuestion] = useState('');
  const [windowDays, setWindowDays] = useState(7);
  const [state, setState] = useState<AskState>('idle');
  const [result, setResult] = useState<AskResponse | null>(null);
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Ask Zmist';
    return () => {
      document.title = prev;
      abortRef.current?.abort();
    };
  }, []);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const q = question.trim();
    if (!q || state === 'working') return;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setState('working');
    setError('');
    setResult(null);
    track('ask_started', { origin_channel_id: channel || undefined });
    try {
      const res = await askApi.ask({ channel: channel || undefined, question: q, windowDays }, ctrl.signal);
      setResult(res);
      setState('done');
      track('ask_answered', { origin_channel_id: channel || undefined });
    } catch (err) {
      if (ctrl.signal.aborted) {
        setState('canceled');
        return;
      }
      setError(err instanceof Error ? err.message : 'Ask failed.');
      setState('failed');
      track('ask_failed', { origin_channel_id: channel || undefined });
    }
  };

  const cancel = () => abortRef.current?.abort();

  return (
    <div className="min-h-screen flex flex-col bg-(--color-bg) text-black dark:text-white">
      <PublicSiteHeader />
      <main className="flex-1 w-full max-w-[820px] mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="text-[28px] font-extrabold tracking-tight m-0 mb-1">Ask Zmist</h1>
        <p className="text-[15px] text-black/60 dark:text-white/60 mt-0 mb-6">
          Questions are answered only from data you can access, with the evidence shown.
        </p>

        {!enabled ? (
          <p role="status" className="text-black/60 dark:text-white/60">
            Ask Zmist isn&apos;t available yet.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-3" aria-label="Context">
              {channel ? (
                <Link
                  to={`/public/${encodeURIComponent(channel)}`}
                  className="rounded-full px-3 py-1 text-[13px] bg-white dark:bg-white/10 border border-black/10 dark:border-white/10 no-underline text-inherit"
                >
                  Channel: {channel}
                </Link>
              ) : (
                <span className="rounded-full px-3 py-1 text-[13px] bg-white dark:bg-white/10 border border-black/10 dark:border-white/10">
                  All public channels
                </span>
              )}
              <label className="text-[13px] flex items-center gap-2">
                Window
                <select
                  value={windowDays}
                  onChange={(e) => setWindowDays(Number(e.target.value))}
                  disabled={state === 'working'}
                  className="rounded-lg px-2 py-1 bg-white dark:bg-white/10 border border-black/10 dark:border-white/10"
                >
                  {WINDOWS.map((d) => (
                    <option key={d} value={d}>
                      Last {d} days
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <form onSubmit={submit} className="flex flex-col gap-3">
              <label htmlFor="ask-question" className="sr-only">
                Your question
              </label>
              <textarea
                id="ask-question"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="What changed in this channel over the last 7 days?"
                className="w-full rounded-2xl p-4 text-[15px] bg-white dark:bg-white/5 border border-black/10 dark:border-white/10"
              />
              <div className="flex gap-2">
                <Button type="submit" disabled={state === 'working' || !question.trim()}>
                  <Sparkles size={16} />
                  Ask
                </Button>
                {state === 'working' && (
                  <Button type="button" variant="bordered" onClick={cancel}>
                    <X size={16} />
                    Cancel
                  </Button>
                )}
              </div>
            </form>

            <div aria-live="polite" className="mt-6">
              {state === 'working' && (
                <div className="flex items-center gap-2 text-black/60 dark:text-white/60">
                  <Loader2 size={18} className="animate-spin" /> Reading data…
                </div>
              )}
              {state === 'canceled' && <p className="text-black/60 dark:text-white/60">Canceled.</p>}
              {state === 'failed' && (
                <p role="alert" className="text-red-500">
                  {error}
                </p>
              )}
              {state === 'done' && result && <AskResult result={result} />}
            </div>
          </>
        )}
      </main>
      <PublicSiteFooter />
    </div>
  );
}

export function AskResult({ result }: { result: AskResponse }) {
  return (
    <div className="flex flex-col gap-4">
      {result.answer && <p className="text-[16px] leading-relaxed m-0">{result.answer}</p>}
      {result.claims.map((c, i) => (
        <div
          key={i}
          className="rounded-2xl p-4 bg-white dark:bg-white/5 border border-black/[0.06] dark:border-white/10"
        >
          <span className="text-[11px] font-semibold uppercase tracking-wider text-(--color-accent)">
            {CLAIM_LABEL[c.kind]}
          </span>
          <p className="m-0 mt-1 text-[15px]">{c.text}</p>
          {c.evidence.length > 0 ? (
            <ul className="list-none p-0 m-0 mt-3 flex flex-col gap-2">
              {c.evidence.map((ev, j) => (
                <li
                  key={j}
                  className="text-[12px] rounded-lg px-3 py-2 bg-black/[0.04] dark:bg-white/[0.06] text-black/70 dark:text-white/70"
                >
                  {ev.channel} · {ev.signalType} · {ev.from} → {ev.to} ·{' '}
                  <strong>
                    {ev.value ?? '—'} {ev.unit}
                  </strong>{' '}
                  · source: {ev.source}
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 mt-2 text-[12px] text-black/50 dark:text-white/50">No linked evidence.</p>
          )}
        </div>
      ))}
      {result.limitations.length > 0 && (
        <div>
          <h2 className="text-[14px] font-semibold m-0 mb-1">Limitations</h2>
          <ul className="m-0 pl-5 text-[13px] text-black/65 dark:text-white/65">
            {result.limitations.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </div>
      )}
      {result.asOf && <p className="text-[12px] text-black/45 dark:text-white/45 m-0">Data as of {result.asOf}</p>}
    </div>
  );
}
